/* ===========================================================================
   Les mailles d'Agat — ROTATION DES CARROUSELS DE COLLECTION
   ---------------------------------------------------------------------------
   Chargé en `defer` par layout/theme.liquid, à côté de lmda.css.

   Un seul rôle : faire avancer tout seul le rail des sections « Collection en
   vedette » quand la collection contient plus de pièces que de colonnes, pour
   qu'on devine qu'il y a la suite derrière. Le cadrage — trois pièces à la
   fois, dans la gouttière de la page — vient du CSS (assets/lmda.css § 7 bis).

   On ne remplace RIEN du carrousel de Dawn : le `<slider-component>`, ses
   flèches et son `scroll-snap` gardent la main. Ce script se contente de
   pousser le rail d'une carte, comme le ferait un clic sur la flèche suivante —
   puis de le ramener au début arrivé au bout.

   Trois garde-fous, dans l'ordre où ils comptent :

     1. rien ne tourne si le visiteur a demandé moins d'animations
        (`prefers-reduced-motion`) ;
     2. rien ne tourne tant que le carrousel n'est pas à l'écran, ni si
        l'onglet est en arrière-plan, ni sous le clavier ;
     3. au glissement horizontal, la rotation s'arrête pour de bon — le
        visiteur explore lui-même, la page ne bouge plus sous ses doigts.

   Script absent ou en erreur : le carrousel reste un carrousel Dawn ordinaire,
   il ne tourne simplement pas.
   =========================================================================== */

(() => {
  'use strict';

  /* Temps d'affichage d'une vue. 5 s : le temps de parcourir trois pièces sans
     avoir l'impression qu'on nous presse. */
  const DELAY = 5000;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** Le rail défile-t-il vraiment ? Faux quand tout tient à l'écran (collection
   *  courte, ou format où Dawn rend une grille au lieu d'un rail). Recalculé à
   *  chaque tour : la réponse change avec la largeur de la fenêtre. */
  const canScroll = (track) => track.scrollWidth - track.clientWidth > 1;

  /** Pas d'un cran : l'écart entre deux cartes visibles. Mesuré à chaque tour
   *  plutôt que mémorisé, pour suivre les changements de format. */
  const stepWidth = (track) => {
    const slides = Array.from(track.querySelectorAll('.slider__slide')).filter(
      (slide) => slide.clientWidth > 0
    );
    if (slides.length < 2) return 0;
    return slides[1].offsetLeft - slides[0].offsetLeft;
  };

  const setup = (component) => {
    const track = component.querySelector('[id^="Slider-"]');
    if (!track) return;

    let timer = null;
    /* Compte des raisons de ne pas tourner (hors écran, onglet caché, focus
       clavier). On ne repart que lorsqu'il n'en reste aucune — sinon la sortie
       d'une raison relancerait le rail malgré les autres. */
    let holds = 0;
    let stopped = false;

    const tick = () => {
      if (!canScroll(track)) return;
      const step = stepWidth(track);
      if (step <= 0) return;

      const end = track.scrollWidth - track.clientWidth;

      /* Le retour au début se décide sur la position ACTUELLE, pas sur celle
         qu'on vise : quatre pièces pour trois places, c'est un pas unique qui
         mène pile à la fin de course — tester la position visée renverrait au
         début dès le premier tour, et le rail ne bougerait jamais.
         1 px de tolérance : les largeurs de cartes tombent rarement rond. */
      const atEnd = track.scrollLeft >= end - 1;
      const next = atEnd ? 0 : Math.min(track.scrollLeft + step, end);

      track.scrollTo({ left: next, behavior: 'smooth' });
    };

    const start = () => {
      if (stopped || holds > 0 || timer) return;
      timer = window.setInterval(tick, DELAY);
    };

    const pause = () => {
      window.clearInterval(timer);
      timer = null;
    };

    const hold = () => {
      holds += 1;
      pause();
    };

    const release = () => {
      holds = Math.max(0, holds - 1);
      start();
    };

    /* Arrêt définitif : le visiteur conduit, on ne reprend pas le volant. */
    const stop = () => {
      stopped = true;
      pause();
    };

    /* PAS de pause au survol, volontairement — c'est le réflexe du carrousel de
       texte, et il ne tient pas ici : sur une page qu'on parcourt à la souris,
       le curseur traîne au milieu de l'écran, donc sur les créations. Le
       carrousel passait son temps en pause, exactement là où on le regarde. Le
       clavier, lui, garde sa pause : le focus suit le rail, le déplacer sous
       les doigts de quelqu'un qui tabule serait pénible. */
    component.addEventListener('focusin', hold);
    component.addEventListener('focusout', release);

    /* Glissement horizontal sur le rail : le visiteur explore lui-même, on lui
       laisse la main pour de bon. On ne retient QUE l'horizontal — un scroll
       vertical de la page, curseur posé sur le carrousel, remonte ici aussi et
       n'a rien demandé au carrousel. */
    track.addEventListener(
      'wheel',
      (event) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) stop();
      },
      { passive: true }
    );

    /* Onglet en arrière-plan : inutile de faire défiler dans le vide. */
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) hold();
      else release();
    });

    /* Hors écran : on part en pause, et le carrousel reprend là où il en était
       quand le visiteur redescend dessus. Rien ne démarre avant le premier
       rapport de l'observateur — qui arrive tout de suite après `observe()` et
       tranche dans les deux sens. Seuil bas : il suffit que le carrousel entre
       dans l'écran, sans attendre qu'il s'y installe. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? release() : hold()));
        },
        { threshold: 0.1 }
      ).observe(component);
    } else {
      start();
    }
  };

  const init = () => {
    if (reducedMotion.matches) return;
    document
      .querySelectorAll('.collection slider-component')
      .forEach(setup);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
